import React from 'react';
import { Save, RotateCcw, CheckCircle, Loader2, Clock } from 'lucide-react';
import { getStatusLabel as getStatusLabelByCode, getStatusColorByCode, SECTION_STATUS } from '../../../../shared/utils/statusConstants';

/**
 * EditorCardFooter — extracted from ContentEditor.jsx (Phase 9 refactor).
 * Card footer: status dot + role-conditional action buttons.
 *
 * Status code logic (preserved exactly):
 *   isAssignedOrProgress: code 51 | 52 | label includes 'assigned'|'in progress'|'in-progress'
 *   isSubmitted:          code 53 | label includes 'submitted'|'awaiting review'
 *   isApproved:           code 54 | label includes 'approved'
 *
 * Button visibility (preserved exactly):
 *   Team Member: Submit to Lead → only if !isSubmitted && isAssignedOrProgress
 *   Team Lead:   Save Draft / Request Changes / Approve Section → only if isSubmitted
 *   Both roles:  Approved badge → only if isApproved
 * Zero logic changes. All handlers passed as props.
 */
const EditorCardFooter = ({
    editableData,
    activeSection,
    selectedSection,
    sectionTitle,
    assignments = {},
    isManager,
    handleUpdate,
    handleSubmitToLead,
    isSubmittingToLead,
    handleToggleSectionApproval,
    isApprovingSection = false,
    setStageModal,
    setStageError,
    isNoGo = false,
    isTeamLeadViewOnly = false
}) => {
    // Resolve Assignment Metadata
    const assignedRaw = assignments[sectionTitle] ||
        assignments[selectedSection?.key] ||
        assignments[selectedSection?.label] ||
        assignments[selectedSection?.id] ||
        assignments[selectedSection?._id] ||
        selectedSection?.assigned_to || [];
    const assignedArray = Array.isArray(assignedRaw) ? assignedRaw : [assignedRaw];
    const memberNames = assignedArray.map(m => typeof m === 'object' ? (m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim() || 'Contributor') : String(m));
    const isAssigned = memberNames.length > 0;

    // Resolve Status Metadata
    const statusObj = selectedSection?.status && typeof selectedSection?.status === 'object' ? selectedSection.status : {};
    const statusLabelRaw = statusObj.label || (typeof selectedSection?.status === 'string' ? selectedSection.status : '');

    let sectionStatus = statusLabelRaw ||
        editableData?.SECTION_STATUS?.[selectedSection?.key] || editableData?.section_status?.[selectedSection?.key] ||
        editableData?.SECTION_STATUS?.[selectedSection?.label] || editableData?.section_status?.[selectedSection?.label] ||
        editableData?.SECTION_STATUS?.[activeSection] || editableData?.section_status?.[activeSection] || '';

    const statusCode = statusObj.code || selectedSection?.status_code || parseInt(sectionStatus) || 0;

    // Resolve display label from central status definitions
    let displayStatus = SECTION_STATUS[statusCode]?.label || (() => {
        const currentStatusLower = String(sectionStatus).toLowerCase();
        if (currentStatusLower.includes('unassigned')) return 'Section Unassigned';
        if (currentStatusLower.includes('session expired') || currentStatusLower.includes('session-expired')) return 'Section Unassigned';
        if (currentStatusLower.includes('auto-closed') || currentStatusLower.includes('autoclosed') || currentStatusLower.includes('auto closed')) return 'Auto-Closed';
        if (currentStatusLower.includes('revision') || currentStatusLower.includes('rejected')) return 'Section Revision Needed';
        if (currentStatusLower.includes('submitted') || currentStatusLower.includes('review')) return 'Section Submitted for Review';
        if (currentStatusLower.includes('approved')) return 'Section TL Approved';
        if (currentStatusLower.includes('progress')) return 'Section In-Progress';
        if (currentStatusLower.includes('assigned') || isAssigned) return 'Section Assigned';
        return sectionStatus || 'Section Unassigned';
    })();

    // Determine dot color from central status definitions
    const dotColor = getStatusColorByCode(statusCode) !== '#6c757d' || SECTION_STATUS[statusCode]
        ? getStatusColorByCode(statusCode)
        : (() => {
            const lowerDisp = displayStatus.toLowerCase();
            if (lowerDisp.includes('approved')) return '#28a745';
            if (lowerDisp.includes('revision')) return '#dc3545';
            if (lowerDisp.includes('submitted')) return '#6610f2';
            if (lowerDisp.includes('progress')) return '#ffc107';
            if (lowerDisp.includes('expired')) return '#343a40';
            if (lowerDisp.includes('closed')) return '#6c757d';
            if (lowerDisp.includes('assigned')) return '#17a2b8';
            return '#6c757d';
        })();

    // Map to proper CSS status class / style
    const lowerStatus = displayStatus.toLowerCase();
    const isApproved = lowerStatus.includes('approved');
    const isRevisionNeeded = lowerStatus.includes('revision');
    const isSubmitted = lowerStatus.includes('submitted') || lowerStatus.includes('review');
    const isProgress = lowerStatus.includes('progress');
    const isAssignedOnly = lowerStatus.includes('assigned');
    const isAssignedOrProgress = isAssignedOnly || isProgress;

    const statusLabel = String(
        editableData?.global_status?.label || 
        editableData?.global_status?.name || 
        editableData?.proposal_stage || 
        editableData?.PROPOSAL_STAGE || 
        editableData?.status || 
        ''
    ).toLowerCase();

    const statusCodeVal = Number(
        editableData?.global_status?.code || 
        editableData?.status_code || 
        editableData?.global_status_code || 
        editableData?.proposal_stage_code || 
        0
    );

    const isProposalExpired = statusCodeVal === 56 || statusLabel.includes('expired');

    const isExpiredOrClosed = statusCode === 56 || statusCode === 57 || lowerStatus.includes('closed') || lowerStatus.includes('expired') || isProposalExpired;

    return (
        <>
            {/* Status indicator */}
            <div className="footer-left-status" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div
                    className={`status-dot ${isAssignedOrProgress || isSubmitted || isRevisionNeeded ? 'pulsing' : ''}`}
                    style={{
                        background: dotColor,
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        cursor: 'help'
                    }}
                    title={`Status: ${displayStatus}`}
                />
                <span style={{
                    fontSize: '0.8rem',
                    fontWeight: '700',
                    color: 'grey',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                }}>
                    {displayStatus}
                </span>
            </div>
 
            {/* Team Member Flow: Show Submit button if NOT submitted and (Assigned/Progress OR Revision Needed) */}
            {!isNoGo && !isManager && (isAssignedOrProgress || isRevisionNeeded) && !isSubmitted && !isApproved && !isExpiredOrClosed && (
                <button
                    className="footer-action-btn st-submit-lead"
                    onClick={handleSubmitToLead}
                    disabled={isSubmittingToLead}
                    style={{
                        background: '#64748B',
                        color: 'white', border: 'none', padding: '8px 24px',
                        borderRadius: '8px', fontWeight: '600', transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => !isSubmittingToLead && (e.currentTarget.style.background = '#475569')}
                    onMouseLeave={(e) => !isSubmittingToLead && (e.currentTarget.style.background = '#64748B')}
                >
                    {isSubmittingToLead ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle size={16} />}
                    <span>Submit to Team Lead</span>
                </button>
            )}

            {/* Team Member Flow: Waiting for Team Lead review badge */}
            {!isNoGo && !isManager && isSubmitted && !isApproved && (
                <div
                    className="footer-status-badge submitted"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 14px',
                        borderRadius: '8px',
                        background: '#FEF3C7',
                        border: '1px solid #FCD34D',
                        color: '#92400E',
                        fontWeight: '600',
                        fontSize: '0.82rem'
                    }}
                >
                    <Clock size={16} color="#D97706" style={{ flexShrink: 0 }} />
                    <span>Waiting for Team Lead review</span>
                </div>
            )}
 
            {/* Team Lead Flow: Show Review buttons only for Submitted */}
            {!isNoGo && isManager && isSubmitted && !isApproved && (
                <div className="footer-right-actions">
                    {/* 
                    <button
                        className="footer-action-btn st-draft"
                        onClick={() => !isTeamLeadViewOnly && handleUpdate()}
                        disabled={isTeamLeadViewOnly}
                        style={{ opacity: isTeamLeadViewOnly ? 0.5 : 1, cursor: isTeamLeadViewOnly ? 'not-allowed' : 'pointer' }}
                    >
                        <Save size={16} />
                        <span>Save Draft</span>
                    </button>
                    */}
                    <button
                        className="footer-action-btn st-reassign"
                        onClick={() => { if (!isTeamLeadViewOnly && !isProposalExpired) { setStageModal({ show: true, type: 'Reassign', notes: '' }); setStageError(""); } }}
                        disabled={isTeamLeadViewOnly || isProposalExpired}
                        style={{
                            opacity: (isTeamLeadViewOnly || isProposalExpired) ? 0.5 : 1,
                            cursor: (isTeamLeadViewOnly || isProposalExpired) ? 'not-allowed' : 'pointer'
                        }}
                    >
                        <RotateCcw size={16} />
                        <span>Request Changes</span>
                    </button>
                     <button
                        className="footer-action-btn st-approve"
                        onClick={() => !isTeamLeadViewOnly && !isProposalExpired && handleToggleSectionApproval(activeSection)}
                        disabled={isTeamLeadViewOnly || isProposalExpired || isApprovingSection}
                        style={{
                            opacity: (isTeamLeadViewOnly || isProposalExpired || isApprovingSection) ? 0.5 : 1,
                            cursor: (isTeamLeadViewOnly || isProposalExpired || isApprovingSection) ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {isApprovingSection ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle size={16} />}
                        <span>{isApprovingSection ? 'Approving...' : 'Approve Section'}</span>
                    </button>
                </div>
            )}

            {/* If Approved, show only a status badge */}
            {isApproved && (
                <div
                    className="footer-status-badge approved"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                    }}
                >
                    <CheckCircle size={16} color="#10B981" style={{ flexShrink: 0 }} />
                    <span style={{ color: '#10B981', fontWeight: '700', fontSize: '0.85rem', lineHeight: '1' }}>Section Approved</span>
                </div>
            )}

            {/* If No-Go, show a beautiful badge explaining that it is View Only */}
            {isNoGo && (
                <div className="footer-status-badge info" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.45rem',
                    padding: '6px 14px',
                    borderRadius: '8px',
                    background: '#F1F5F9',
                    border: '1px solid #E2E8F0'
                }}>
                    <span style={{ fontSize: '15px' }}>ℹ️</span>
                    <span style={{ color: '#64748B', fontWeight: '700', fontSize: '0.8rem', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                        Proposal Decision: No-Go (View Only Mode)
                    </span>
                </div>
            )}
        </>
    );
};

export default EditorCardFooter;
